/**
 * 保险库解锁面板：主密码解锁 / 恢复码解锁。
 * 契约缺口：无预解锁取 kdfParams 端点，新设备（localStorage 无 lc-vault-kdf）无法解锁，
 * 此处给出优雅提示（VaultKdfMissingError 场景）。
 */
import { useState } from 'react';
import { useVault } from '../stores/vault';
import { Button, Card, ErrorBox, Field, Input, Tabs, errMsg } from './ui';
import { Icon } from './icons';

export function UnlockPanel() {
  const { unlock, recover, hasLocalKdf, clearLocalKdf } = useVault();
  const [mode, setMode] = useState<'password' | 'recovery'>('password');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      if (mode === 'password') {
        if (!password) {
          setError('请输入主密码');
          return;
        }
        await unlock(password);
      } else {
        if (!code.trim()) {
          setError('请输入恢复码');
          return;
        }
        await recover(code.trim());
      }
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card className="mx-auto max-w-md p-6">
      <div className="mb-5 text-center">
        <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-xl bg-indigo-600 text-white">
          <Icon name="key" className="h-6 w-6" />
        </div>
        <h2 className="text-lg font-semibold">解锁保险库</h2>
        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">输入主密码在本机派生密钥并解锁</p>
      </div>

      {!hasLocalKdf && (
        <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-200">
          当前设备没有本机密钥参数缓存，无法直接解锁。请回到此前解锁过的设备，
          或在设置中导出/迁移密钥参数。（服务端未提供预解锁获取 kdfParams 的端点）
        </div>
      )}

      <div className="mb-4">
        <Tabs
          tabs={[
            { key: 'password', label: '主密码' },
            { key: 'recovery', label: '恢复码' },
          ]}
          active={mode}
          onChange={setMode}
        />
      </div>

      <ErrorBox error={error} className="mb-4" />

      <div className="space-y-4">
        {mode === 'password' ? (
          <Field label="主密码" required>
            <Input
              type="password"
              autoFocus
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && submit()}
            />
          </Field>
        ) : (
          <Field label="恢复码" required hint="24 位恢复码，格式 XXXX-XXXX-XXXX-XXXX-XXXX-XXXX">
            <Input
              value={code}
              autoFocus
              onChange={(e) => setCode(e.target.value)}
              placeholder="XXXX-XXXX-XXXX-XXXX-XXXX-XXXX"
              className="font-mono"
              onKeyDown={(e) => e.key === 'Enter' && submit()}
            />
          </Field>
        )}
        <Button className="w-full" onClick={submit} loading={busy}>
          解锁
        </Button>
        <button
          type="button"
          className="w-full text-center text-xs text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
          onClick={() => clearLocalKdf()}
        >
          清除本机密钥缓存并重新初始化
        </button>
      </div>
    </Card>
  );
}
